import { LocalStorageKeys, SessionStorageKeys } from '@constants/enums';
import { CognitoPermission } from './types';

/**
 * Store access token in sessionStorage (cleared on browser close)
 */
const setIdToken = (token: string): void => {
  try {
    sessionStorage.setItem(SessionStorageKeys.ID_TOKEN, token);
  } catch (error) {
    console.error('Error storing id token:', error);
  }
};

const getIdToken = (): string | null => {
  try {
    return sessionStorage.getItem(SessionStorageKeys.ID_TOKEN);
  } catch (error) {
    console.error('Error retrieving id token:', error);
    return null;
  }
};

const setAccessToken = (token: string): void => {
  try {
    sessionStorage.setItem(SessionStorageKeys.ACCESS_TOKEN, token);
  } catch (error) {
    console.error('Error storing access token:', error);
  }
};

/**
 * Get access token from sessionStorage
 */
const getAccessToken = (): string | null => {
  try {
    return sessionStorage.getItem(SessionStorageKeys.ACCESS_TOKEN);
  } catch (error) {
    console.error('Error retrieving access token:', error);
    return null;
  }
};

/**
 * Store Cognito refresh token in sessionStorage
 */
const setRefreshToken = (token: string): void => {
  try {
    sessionStorage.setItem(SessionStorageKeys.REFRESH_TOKEN, token);
  } catch (error) {
    console.error('Error storing refresh token:', error);
  }
};

/**
 * Get Cognito refresh token from sessionStorage
 */
const getRefreshToken = (): string | null => {
  try {
    return sessionStorage.getItem(SessionStorageKeys.REFRESH_TOKEN);
  } catch (error) {
    console.error('Error retrieving refresh token:', error);
    return null;
  }
};

/**
 * Store user roles in sessionStorage
 */
const setRoles = (roles: string[]): void => {
  try {
    sessionStorage.setItem(SessionStorageKeys.ROLES, JSON.stringify(roles));
  } catch (error) {
    console.error('Error storing roles:', error);
  }
};

/**
 * Get user roles from sessionStorage
 */
const getRoles = (): string[] => {
  try {
    const roles = sessionStorage.getItem(SessionStorageKeys.ROLES);
    return roles ? JSON.parse(roles) : [];
  } catch (error) {
    console.error('Error retrieving roles:', error);
    return [];
  }
};

/**
 * Store user permissions in sessionStorage
 */
const setPermissions = (permissions: CognitoPermission[]): void => {
  try {
    sessionStorage.setItem(
      SessionStorageKeys.PERMISSIONS,
      JSON.stringify(permissions)
    );
  } catch (error) {
    console.error('Error storing permissions:', error);
  }
};

/**
 * Get user permissions from sessionStorage
 */
const getPermissions = (): CognitoPermission[] => {
  try {
    const permissions = sessionStorage.getItem(SessionStorageKeys.PERMISSIONS);
    return permissions ? JSON.parse(permissions) : [];
  } catch (error) {
    console.error('Error retrieving permissions:', error);
    return [];
  }
};

/**
 * Store user KYC status in sessionStorage for Cognito
 */
const setUserKyc = (isKyc: boolean): void => {
  try {
    localStorage.setItem(LocalStorageKeys.IS_KYC, String(isKyc));
  } catch (error) {
    console.error('Error storing KYC status:', error);
  }
};

/**
 * Get user KYC status from sessionStorage for Cognito
 */
const getUserKyc = (): boolean => {
  try {
    return localStorage.getItem(LocalStorageKeys.IS_KYC) === 'true';
  } catch (error) {
    console.error('Error retrieving KYC status:', error);
    return false;
  }
};

/**
 * Store cognitoUuid in sessionStorage for token refresh operations
 */
const setCognitoUuid = (cognitoUuid: string): void => {
  try {
    sessionStorage.setItem(SessionStorageKeys.COGNITO_UUID, cognitoUuid);
  } catch (error) {
    console.error('Error storing cognitoUuid:', error);
  }
};

/**
 * Get cognitoUuid from sessionStorage for token refresh operations
 */
const getCognitoUuid = (): string | null => {
  try {
    return sessionStorage.getItem(SessionStorageKeys.COGNITO_UUID);
  } catch (error) {
    console.error('Error retrieving cognitoUuid:', error);
    return null;
  }
};

/**
 * Clear all Cognito authentication data
 */
const clearAuthData = (): void => {
  try {
    sessionStorage.removeItem(SessionStorageKeys.ID_TOKEN);
    sessionStorage.removeItem(SessionStorageKeys.ACCESS_TOKEN);
    sessionStorage.removeItem(SessionStorageKeys.ROLES);
    sessionStorage.removeItem(SessionStorageKeys.PERMISSIONS);
    sessionStorage.removeItem(SessionStorageKeys.REFRESH_TOKEN);
    sessionStorage.removeItem(SessionStorageKeys.COGNITO_UUID);
  } catch (error) {
    console.error('Error clearing auth data:', error);
  }
};

/**
 * Check if user has valid session data
 */
const hasValidSession = (): boolean => {
  const token = getAccessToken();
  const roles = getRoles();
  return !!(token && roles.length > 0);
};

/**
 * Service for handling secure storage of authentication data
 */
const storageService = {
  setIdToken,
  getIdToken,
  setAccessToken,
  getAccessToken,
  setRefreshToken,
  getRefreshToken,
  setRoles,
  getRoles,
  setPermissions,
  getPermissions,
  setUserKyc,
  getUserKyc,
  setCognitoUuid,
  getCognitoUuid,
  clearAuthData,
  hasValidSession,
};

export default storageService;
